import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getLocalApiUrl } from '../config/api'
import AuthShell, { AuthField, AuthSubmitButton } from '../components/AuthShell'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [validating, setValidating] = useState(true)
  const [valid, setValid] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function validate() {
      if (!token) {
        setValidating(false)
        setValid(false)
        return
      }
      try {
        const res = await fetch(
          getLocalApiUrl(`/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
        )
        const j = await res.json()
        if (!cancelled) {
          setValid(Boolean(j.valid))
          setUsername(j.username || '')
        }
      } catch (_) {
        if (!cancelled) setValid(false)
      } finally {
        if (!cancelled) setValidating(false)
      }
    }
    validate()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      toast.error('Passwords do not match')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(getLocalApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) {
        throw new Error(j.error || 'Failed to reset password')
      }
      toast.success(j.message || 'Password updated. Please sign in.')
      navigate('/login', { replace: true })
    } catch (err) {
      toast.error(err.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  if (validating) {
    return (
      <AuthShell title="Set your password" subtitle="Checking your link…">
        <p className="text-sm text-gray-600 text-center">Please wait…</p>
      </AuthShell>
    )
  }

  if (!valid) {
    return (
      <AuthShell title="Link expired" subtitle="This password link is invalid or has expired.">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Request a new link from the sign-in page.
          </p>
          <Link
            to="/forgot-password"
            className="block text-center text-sm font-medium"
            style={{ color: '#0A5169' }}
          >
            Forgot password
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Set your password"
      subtitle={username ? `Account: ${username}` : 'Choose a new password for your account'}
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        <AuthField
          label="New password"
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          autoComplete="new-password"
        />
        <AuthField
          label="Confirm password"
          id="confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter your password"
          autoComplete="new-password"
        />
        <AuthSubmitButton loading={loading}>Save password</AuthSubmitButton>
      </form>
    </AuthShell>
  )
}
